import React, { useState } from 'react';
import { useLabResultsConfig } from './useLabResultsConfig';
import { LabResultsItem } from './LabResultsItem';
import { useIsFetchingMedicalResults } from './hooks/useIsFetchingMedicalResults';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Search } from './providers/Search';
import { Selection } from './providers/Selection';
import { Sidebar } from './Sidebar';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { MedicalResultHistory } from './hooks/useFetchMedicalResultHistories';
import RangeViewModalV2 from '../Measurements/Graphs/Ranges/RangeViewModalV2';
import { MedicalResultSource } from '../../../../services/MedicalResultsService';

const useStyles = makeStyles(theme => ({
  container: {
    boxShadow: theme.boxShadows.table,
    display: 'flex',
    height: '100%',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  sidebar: {
    backgroundColor: theme.palette.background.default,
    borderLeft: `1px solid ${theme.palette.divider}`,
    overflowY: 'auto',
    width: '200px',
    height: '100%',
  },
  labResultsContainer: {
    flex: 1,
    overflow: 'auto',
    height: '100%',
    minWidth: 0,
  },
}));

type LabResultsProps = {
  patient: fhir.Patient;
};

function LabResults({ patient }: LabResultsProps) {
  const classes = useStyles();
  const [selectedMedicalResultHistory, setSelectedMedicalResultHistory] =
    useState<{
      history: MedicalResultHistory;
      source?: MedicalResultSource;
      initialSelectedResourceId?: string;
      initialSourceDocument?: fhir.DocumentReference;
      preferPrefetchedMedicalResults?: boolean;
    } | null>(null);

  const isFetchingDimensionData = useIsFetchingMedicalResults(
    patient.id as string,
  );

  const labResultsConfig = useLabResultsConfig();

  if (isFetchingDimensionData) {
    return (
      <Box justify="center" align="center" fullWidth fullHeight>
        <DotLoader />
      </Box>
    );
  }

  return (
    <Search.Provider>
      <Selection.Provider>
        <Box className={classes.container}>
          <Box className={classes.labResultsContainer} direction="column">
            {labResultsConfig.map(item => (
              <LabResultsItem
                key={item.title}
                patient={patient}
                labResultsConfigItem={item}
                onClick={(value, options) => {
                  setSelectedMedicalResultHistory({
                    history: value,
                    source: item.source,
                    preferPrefetchedMedicalResults:
                      options?.preferPrefetchedMedicalResults,
                  });
                }}
                onOpenSourceDocument={(document, history, resourceId) => {
                  setSelectedMedicalResultHistory({
                    history,
                    source: item.source,
                    initialSelectedResourceId: resourceId,
                    initialSourceDocument: document,
                    preferPrefetchedMedicalResults: true,
                  });
                }}
              />
            ))}
          </Box>
          <Sidebar patient={patient} className={classes.sidebar} />
        </Box>
        {selectedMedicalResultHistory && (
          <RangeViewModalV2
            key={`${selectedMedicalResultHistory.history.dimension.id}-${
              selectedMedicalResultHistory.history.medicalResultHistory[0]
                ?.resource?.id ?? ''
            }`}
            isOpen={Boolean(selectedMedicalResultHistory)}
            onClose={() => setSelectedMedicalResultHistory(null)}
            dimension={selectedMedicalResultHistory.history.dimension}
            patient={patient}
            initialSelectedResourceId={
              selectedMedicalResultHistory.initialSelectedResourceId ??
              selectedMedicalResultHistory.history.medicalResultHistory[0]
                ?.resource?.id
            }
            initialSourceDocument={
              selectedMedicalResultHistory.initialSourceDocument
            }
            prefetchedMedicalResults={
              selectedMedicalResultHistory.history.medicalResultHistory
            }
            preferPrefetchedMedicalResults={
              selectedMedicalResultHistory.preferPrefetchedMedicalResults
            }
            source={selectedMedicalResultHistory.source}
          />
        )}
      </Selection.Provider>
    </Search.Provider>
  );
}
export default React.memo(LabResults);
